// Lean V4 campaign: the persistent strategy-layer state for the supply-run game,
// built fresh rather than carrying V3 campaign.mjs's baggage (territories,
// vehicles, V3 missions, resources). See plans/V4_APP_REWRITE_PLAN.md phase 3.
//
// It holds only what the V4 loop needs: the survivor roster, each survivor's
// equipped items, base storage, per-POI threat (threat.mjs),
// a base coordinate supply runs route from, and a day counter.
//
// Survivors are shaped { id, name, status, ... } so permadeath.mjs can apply
// supply-run deaths through the shared `survivorsById` data shape without importing
// the old V3 campaign graph.

import {
  canEquipItemId,
  equipmentSlotsAvailable,
  MAX_EQUIPPED_ITEMS,
  normalizeEquipmentItemIds,
} from "./survivor-upgrades.mjs";
import {
  addGlobalThreat,
  createGlobalThreat,
  decayGlobalThreat,
  decayThreat,
  hasReachedGlobalThreshold,
  normalizeGlobalThreat,
  poiThreat,
  recordPoiVisit,
  threatToEnemyCount,
} from "./threat.mjs";
import { SURVIVOR_MAX_HEALTH } from "./combat-math.mjs";
import { baseRoomStats, normalizeBaseRooms } from "./base-rooms.mjs";
import { getItem, inventoryMaterials, inventoryValue, isFood, isMaterial, inventoryNutrition } from "./items.mjs";
import { dailyQuota, resolveDailyFeeding } from "./survival.mjs";
import { normalizeTutorialState } from "./tutorial-state.mjs";

export const CAMPAIGN_V4_SCHEMA_VERSION = 1;
export const ENDGAME_BRIEFING_DAY = 2;
export const ENDGAME_RESCUE_DAY = 20;

export function createCampaignV4({ campaignId, createdAt, worldSeed, survivors = [], base = null, tutorial = null }) {
  if (!campaignId) throw new Error("createCampaignV4 requires a campaignId.");
  if (!createdAt) throw new Error("createCampaignV4 requires a createdAt.");
  if (!Number.isInteger(worldSeed)) throw new Error("createCampaignV4 requires an integer worldSeed.");
  return normalizeCampaignV4({
    schemaVersion: CAMPAIGN_V4_SCHEMA_VERSION,
    campaignId,
    createdAt,
    worldSeed,
    day: 1,
    survivorsById: Object.fromEntries(survivors.map((survivor) => [survivor.id, normalizeSurvivor(survivor)])),
    survivorProgress: { bySurvivorId: {} },
    poiThreatState: {},
    globalThreat: createGlobalThreat(),
    // Lifetime threat generated across the whole campaign (never decays). The
    // decaying globalThreat gauge sizes the mid-game waves; this accumulator
    // sizes the day-20 final assault, so a campaign of reckless supply runs pays
    // for it at the climax even after the gauge has cooled.
    threatAccrued: 0,
    baseInventory: { items: [], spentValue: 0 },
    pantry: 0,
    materials: 0,
    discoveredPoiIds: [],
    buildingIntelById: {},
    tutorial: normalizeTutorialState(tutorial),
    endgame: normalizeEndgame(),
    base: normalizeBase(base),
  });
}

function normalizeSurvivor(survivor) {
  return {
    ...survivor,
    id: survivor.id,
    name: survivor.name ?? survivor.id,
    status: survivor.status ?? "available",
    hunger: Number.isInteger(survivor.hunger) ? survivor.hunger : 0,
    health: normalizeSurvivorHealth(survivor.health),
    backgroundId: survivor.backgroundId ?? null,
    equipmentItemIds: normalizeEquipmentItemIds(survivor.equipmentItemIds),
    inventory: Array.isArray(survivor.inventory) ? survivor.inventory.filter((itemId) => typeof itemId === "string") : [],
  };
}

// Combat HP. Old saves (no health) backfill to full; a stored value is clamped
// into [0, max]. `max` is also capped to the current SURVIVOR_MAX_HEALTH, so a
// save written when survivors had more HP (e.g. the old 5) migrates down to the
// new max rather than keeping stale, too-many pips. Death in defense can't
// happen (doorway combat retreats at 1 HP), so current stays >= 1 in practice,
// but clamp to >= 0 defensively.
function normalizeSurvivorHealth(health) {
  const max = Number.isInteger(health?.max) && health.max > 0 ? Math.min(health.max, SURVIVOR_MAX_HEALTH) : SURVIVOR_MAX_HEALTH;
  const current = Number.isInteger(health?.current) ? health.current : max;
  return { current: Math.max(0, Math.min(max, current)), max };
}

// Back-fill any missing sub-objects so a save written by an older V4 build
// loads into the current shape (forward-compat within V4).
export function normalizeCampaignV4(campaign) {
  const next = { ...campaign };
  next.schemaVersion = CAMPAIGN_V4_SCHEMA_VERSION;
  next.day = Number.isInteger(next.day) ? next.day : 1;
  next.survivorsById = Object.fromEntries(
    Object.entries(next.survivorsById ?? {}).map(([id, survivor]) => [id, normalizeSurvivor(survivor)]),
  );
  next.survivorProgress = {
    bySurvivorId: {},
  };
  // Migrate the pre-rename fields (heat -> threat) so a campaign saved before
  // the rename keeps its accumulated pressure instead of resetting to cold.
  next.poiThreatState = { ...(next.poiThreatState ?? next.poiHeatState ?? {}) };
  next.globalThreat = normalizeGlobalThreat(next.globalThreat ?? next.globalHeat);
  next.threatAccrued = Number.isFinite(next.threatAccrued) ? Math.max(0, next.threatAccrued) : 0;
  delete next.poiHeatState;
  delete next.globalHeat;
  next.baseInventory = { items: [...(next.baseInventory?.items ?? [])], spentValue: next.baseInventory?.spentValue ?? 0 };
  next.pantry = Number.isFinite(next.pantry) ? next.pantry : 0;
  next.materials = Number.isFinite(next.materials) ? next.materials : 0;
  next.discoveredPoiIds = Array.isArray(next.discoveredPoiIds) ? [...next.discoveredPoiIds] : [];
  next.buildingIntelById = normalizeBuildingIntelById(next.buildingIntelById);
  next.tutorial = normalizeTutorialState(next.tutorial);
  next.endgame = normalizeEndgame(next.endgame);
  next.base = normalizeBase(next.base);
  return next;
}

function normalizeBuildingIntelById(intelById = {}) {
  return Object.fromEntries(Object.entries(intelById ?? {})
    .filter(([id, report]) => typeof id === "string" && id && report)
    .map(([id, report]) => [id, {
      buildingId: id,
      scavengeState: ["promising", "stocked", "emptied", "unknown"].includes(report.scavengeState) ? report.scavengeState : "unknown",
      itemCount: Math.max(0, Number.isInteger(report.itemCount) ? report.itemCount : 0),
      promising: Boolean(report.promising),
      scoutedAtDay: Number.isInteger(report.scoutedAtDay) ? report.scoutedAtDay : null,
      zoneId: typeof report.zoneId === "string" ? report.zoneId : null,
    }]));
}

function normalizeEndgame(endgame = {}) {
  const rescueDay = Number.isInteger(endgame?.rescueDay) && endgame.rescueDay >= ENDGAME_BRIEFING_DAY
    ? endgame.rescueDay
    : ENDGAME_RESCUE_DAY;
  const outcome = endgame?.outcome === "won" || endgame?.outcome === "lost" ? endgame.outcome : null;
  return {
    rescueDay,
    briefingAcknowledged: Boolean(endgame?.briefingAcknowledged),
    // Terminal state: once the day-20 defense resolves the run is over
    // (win or lose) and can't be advanced past.
    rescueResolved: Boolean(endgame?.rescueResolved) || outcome !== null,
    outcome,
  };
}

function normalizeBase(base) {
  if (!base?.coordinate) return null;
  const grid = normalizeBaseGrid(base.grid);
  const defense = normalizeBaseDefense(base.defense);
  return {
    coordinate: [...base.coordinate],
    rooms: normalizeBaseRooms(base.rooms),
    ...(typeof base.buildingId === "string" && base.buildingId ? { buildingId: base.buildingId } : {}),
    ...(typeof base.buildingName === "string" && base.buildingName ? { buildingName: base.buildingName } : {}),
    ...(typeof base.buildingKind === "string" && base.buildingKind ? { buildingKind: base.buildingKind } : {}),
    ...(grid ? { grid } : {}),
    ...(defense ? { defense } : {}),
  };
}

// The auto-generated base-defense layout (base-layout.mjs), persisted as plain
// arrays of string edge keys + a safe-room cell key. normalizeBase does NOT
// spread `base`, so this whitelist is what keeps the layout alive across a
// save/load round-trip — without it the field is silently dropped on load.
function normalizeBaseDefense(defense) {
  if (!defense) return null;
  const wallEdgeKeys = Array.isArray(defense.wallEdgeKeys) ? defense.wallEdgeKeys.filter((key) => typeof key === "string") : null;
  const doorEdgeKeys = Array.isArray(defense.doorEdgeKeys) ? defense.doorEdgeKeys.filter((key) => typeof key === "string") : null;
  const safeRoomCellKey = typeof defense.safeRoomCellKey === "string" ? defense.safeRoomCellKey : null;
  if (!wallEdgeKeys || !doorEdgeKeys || !safeRoomCellKey) return null;
  return { wallEdgeKeys: [...wallEdgeKeys], doorEdgeKeys: [...doorEdgeKeys], safeRoomCellKey };
}

function normalizeBaseGrid(grid) {
  if (!grid || !Array.isArray(grid.originCoordinate)) return null;
  const originCoordinate = grid.originCoordinate.map(Number);
  const numeric = {
    version: Number(grid.version),
    nominalCellSizeMeters: Number(grid.nominalCellSizeMeters),
    cellSizeMeters: Number(grid.cellSizeMeters),
    shrinkPercent: Number(grid.shrinkPercent),
    angleDegrees: Number(grid.angleDegrees),
    angleStepDegrees: Number(grid.angleStepDegrees),
    cols: Number(grid.cols),
    rows: Number(grid.rows),
    insideCells: Number(grid.insideCells),
    coverage: Number(grid.coverage),
  };
  if (!originCoordinate.every(Number.isFinite)
    || !Object.values(numeric).every(Number.isFinite)
    || numeric.cellSizeMeters <= 0
    || numeric.nominalCellSizeMeters <= 0) {
    return null;
  }
  return { originCoordinate, ...numeric };
}

// --- POI discovery (map fog: POIs start as "?" until you search nearby or
// pass them en route) --------------------------------------------------------

export function isPoiDiscovered(campaign, poiId) {
  return (campaign.discoveredPoiIds ?? []).includes(poiId);
}

// Mark POIs discovered; returns a new campaign only if something changed.
export function discoverPois(campaign, poiIds) {
  const discovered = new Set(campaign.discoveredPoiIds ?? []);
  let added = false;
  for (const id of poiIds ?? []) {
    if (id && !discovered.has(id)) { discovered.add(id); added = true; }
  }
  return added ? { ...campaign, discoveredPoiIds: [...discovered] } : campaign;
}

export function recordBuildingScouting(campaign, reports, { zoneId = null } = {}) {
  const current = normalizeBuildingIntelById(campaign.buildingIntelById);
  let changed = false;
  const next = { ...current };
  for (const report of reports ?? []) {
    const id = typeof report?.buildingId === "string" ? report.buildingId : "";
    if (!id) continue;
    const normalized = normalizeBuildingIntelById({
      [id]: {
        ...report,
        zoneId: report.zoneId ?? zoneId,
        scoutedAtDay: campaign.day,
      },
    })[id];
    if (JSON.stringify(current[id] ?? null) !== JSON.stringify(normalized)) {
      next[id] = normalized;
      changed = true;
    }
  }
  return changed ? { ...campaign, buildingIntelById: next } : campaign;
}

export function getBuildingScouting(campaign, buildingId) {
  return normalizeBuildingIntelById(campaign.buildingIntelById)[buildingId] ?? null;
}

// --- Survivor roster ----------------------------------------------------

export function getSurvivorV4(campaign, survivorId) {
  return campaign.survivorsById[survivorId] ?? null;
}

export function activeSurvivorsV4(campaign) {
  return Object.values(campaign.survivorsById).filter((survivor) => survivor.status !== "dead");
}

export function availableSurvivorsV4(campaign) {
  return activeSurvivorsV4(campaign).filter((survivor) => survivor.status === "available");
}

export function setSurvivorV4Status(campaign, survivorId, status) {
  const survivor = getSurvivorV4(campaign, survivorId);
  if (!survivor) return campaign;
  return {
    ...campaign,
    survivorsById: { ...campaign.survivorsById, [survivorId]: { ...survivor, status } },
  };
}

// Add a survivor recruited in the field (see survivor-building.mjs / the supply-run
// recruit flow) to the roster as "available". A no-op if the id is already on
// the roster, so re-applying a supply-run outcome can't duplicate a recruit.
export function recruitSurvivorV4(campaign, { id, name, backgroundId = null } = {}) {
  if (!id || campaign.survivorsById[id]) return campaign;
  return {
    ...campaign,
    survivorsById: {
      ...campaign.survivorsById,
      [id]: normalizeSurvivor({ id, name, backgroundId, status: "available" }),
    },
  };
}

export function equipSurvivorItem(campaign, survivorId, itemId) {
  const survivor = getSurvivorV4(campaign, survivorId);
  if (!survivor) return { ok: false, reason: "unknown-survivor", campaign };
  if (survivor.status === "dead") return { ok: false, reason: "dead-survivor", campaign };
  if (!getItem(itemId)?.equipment) return { ok: false, reason: "not-equipment", campaign };
  if (equipmentSlotsAvailable(survivor) <= 0) return { ok: false, reason: "no-slot", campaign };
  if (!canEquipItemId(survivor.equipmentItemIds, itemId)) return { ok: false, reason: "slot-filled", campaign };

  const storageItems = [...(campaign.baseInventory?.items ?? [])];
  const storageIndex = storageItems.indexOf(itemId);
  if (storageIndex === -1) return { ok: false, reason: "not-in-storage", campaign };
  storageItems.splice(storageIndex, 1);

  const equipped = normalizeEquipmentItemIds(survivor.equipmentItemIds);
  return {
    ok: true,
    reason: null,
    campaign: {
      ...campaign,
      survivorsById: {
        ...campaign.survivorsById,
        [survivorId]: { ...survivor, equipmentItemIds: [...equipped, itemId].slice(0, MAX_EQUIPPED_ITEMS) },
      },
      baseInventory: { ...campaign.baseInventory, items: storageItems },
    },
  };
}

export function unequipSurvivorItem(campaign, survivorId, itemIndex) {
  const survivor = getSurvivorV4(campaign, survivorId);
  if (!survivor) return { ok: false, reason: "unknown-survivor", campaign };
  const equipped = normalizeEquipmentItemIds(survivor.equipmentItemIds);
  if (!Number.isInteger(itemIndex) || itemIndex < 0 || itemIndex >= equipped.length) {
    return { ok: false, reason: "unknown-item", campaign };
  }
  const itemId = equipped[itemIndex];
  const nextEquipped = [...equipped];
  nextEquipped.splice(itemIndex, 1);
  return {
    ok: true,
    reason: null,
    campaign: {
      ...campaign,
      survivorsById: {
        ...campaign.survivorsById,
        [survivorId]: { ...survivor, equipmentItemIds: nextEquipped },
      },
      baseInventory: { ...campaign.baseInventory, items: [...(campaign.baseInventory?.items ?? []), itemId] },
    },
  };
}

export function equipSurvivorInventoryItem(campaign, sourceSurvivorId, inventoryIndex, targetSurvivorId = sourceSurvivorId) {
  const source = getSurvivorV4(campaign, sourceSurvivorId);
  const target = getSurvivorV4(campaign, targetSurvivorId);
  if (!source || !target) return { ok: false, reason: "unknown-survivor", campaign };
  if (source.status === "dead" || target.status === "dead") return { ok: false, reason: "dead-survivor", campaign };
  const inventory = [...(source.inventory ?? [])];
  if (!Number.isInteger(inventoryIndex) || inventoryIndex < 0 || inventoryIndex >= inventory.length) {
    return { ok: false, reason: "unknown-item", campaign };
  }
  const itemId = inventory[inventoryIndex];
  if (!getItem(itemId)?.equipment) return { ok: false, reason: "not-equipment", campaign };
  if (equipmentSlotsAvailable(target) <= 0) return { ok: false, reason: "no-slot", campaign };
  if (!canEquipItemId(target.equipmentItemIds, itemId)) return { ok: false, reason: "slot-filled", campaign };

  inventory.splice(inventoryIndex, 1);
  const equipped = normalizeEquipmentItemIds(target.equipmentItemIds);
  const survivorsById = { ...campaign.survivorsById };
  survivorsById[sourceSurvivorId] = { ...source, inventory };
  const latestTarget = sourceSurvivorId === targetSurvivorId ? survivorsById[targetSurvivorId] : target;
  survivorsById[targetSurvivorId] = {
    ...latestTarget,
    equipmentItemIds: [...equipped, itemId].slice(0, MAX_EQUIPPED_ITEMS),
  };
  return { ok: true, reason: null, itemId, campaign: { ...campaign, survivorsById } };
}

export function transferSurvivorEquipmentItem(campaign, sourceSurvivorId, itemIndex, targetSurvivorId) {
  const source = getSurvivorV4(campaign, sourceSurvivorId);
  const target = getSurvivorV4(campaign, targetSurvivorId);
  if (!source || !target) return { ok: false, reason: "unknown-survivor", campaign };
  if (source.status === "dead" || target.status === "dead") return { ok: false, reason: "dead-survivor", campaign };
  if (sourceSurvivorId === targetSurvivorId) return { ok: false, reason: "same-survivor", campaign };
  const sourceEquipped = normalizeEquipmentItemIds(source.equipmentItemIds);
  if (!Number.isInteger(itemIndex) || itemIndex < 0 || itemIndex >= sourceEquipped.length) {
    return { ok: false, reason: "unknown-item", campaign };
  }
  const itemId = sourceEquipped[itemIndex];
  if (equipmentSlotsAvailable(target) <= 0) return { ok: false, reason: "no-slot", campaign };
  if (!canEquipItemId(target.equipmentItemIds, itemId)) return { ok: false, reason: "slot-filled", campaign };

  const nextSourceEquipped = [...sourceEquipped];
  nextSourceEquipped.splice(itemIndex, 1);
  const targetEquipped = normalizeEquipmentItemIds(target.equipmentItemIds);
  return {
    ok: true,
    reason: null,
    itemId,
    campaign: {
      ...campaign,
      survivorsById: {
        ...campaign.survivorsById,
        [sourceSurvivorId]: { ...source, equipmentItemIds: nextSourceEquipped },
        [targetSurvivorId]: { ...target, equipmentItemIds: [...targetEquipped, itemId].slice(0, MAX_EQUIPPED_ITEMS) },
      },
    },
  };
}

// --- Base item storage --------------------------------------------------

export function storeRecoveredItems(campaign, itemIds) {
  if (!itemIds?.length) return campaign;
  return { ...campaign, baseInventory: { ...campaign.baseInventory, items: [...campaign.baseInventory.items, ...itemIds] } };
}

// Gross value of non-food/non-material items in base storage.
export function baseInventoryValue(campaign) {
  return inventoryValue(campaign.baseInventory.items);
}

// Value of non-food/non-material item storage. Kept for HUD/playtest
// compatibility, but it is no longer spendable upgrade currency.
export function availableEquipmentValue(campaign) {
  return baseInventoryValue(campaign);
}

// --- Pantry (the survival food stockpile) -------------------------------

// Food units currently stockpiled at base. Surplus carries over day to day, so
// a big haul banks against a lean day.
export function pantryFood(campaign) {
  return campaign.pantry ?? 0;
}

export function addToPantry(campaign, nutrition) {
  if (!nutrition) return campaign;
  return { ...campaign, pantry: (campaign.pantry ?? 0) + nutrition };
}

// --- Materials (base-building stockpile) --------------------------------

export function baseMaterials(campaign) {
  return campaign.materials ?? 0;
}

export function addMaterials(campaign, amount) {
  if (!amount) return campaign;
  return { ...campaign, materials: (campaign.materials ?? 0) + amount };
}

export function carriedInventory(campaign) {
  return activeSurvivorsV4(campaign).flatMap((survivor) => survivor.inventory ?? []);
}

export function depositCarriedInventory(campaign) {
  const survivorsById = { ...campaign.survivorsById };
  const items = [];
  for (const [id, survivor] of Object.entries(survivorsById)) {
    if (survivor.status === "dead") continue;
    const inventory = survivor.inventory ?? [];
    if (!inventory.length) continue;
    items.push(...inventory);
    survivorsById[id] = { ...survivor, inventory: [] };
  }
  const spendableItems = items.filter((itemId) => !isFood(itemId) && !isMaterial(itemId));
  const foodGained = inventoryNutrition(items.filter(isFood));
  const materialsGained = inventoryMaterials(items.filter(isMaterial));
  let next = { ...campaign, survivorsById };
  next = storeRecoveredItems(next, spendableItems);
  next = addToPantry(next, foodGained);
  next = addMaterials(next, materialsGained);
  return {
    campaign: next,
    summary: {
      items,
      spendableItems,
      foodGained,
      materialsGained,
    },
  };
}

export function spendMaterials(campaign, amount) {
  if (!Number.isFinite(amount) || amount <= 0) return { ok: false, reason: "invalid-amount", campaign };
  if ((campaign.materials ?? 0) < amount) return { ok: false, reason: "insufficient-materials", campaign };
  return { ok: true, reason: null, campaign: { ...campaign, materials: (campaign.materials ?? 0) - amount } };
}

// --- Base room stats -----------------------------------------------------

export function campaignBaseRoomStats(campaign) {
  return baseRoomStats(campaign.base);
}

export function campaignHousingCapacity(campaign) {
  return campaignBaseRoomStats(campaign).housingCapacity;
}

// True when the active roster is larger than the base's housing capacity.
// SOFT CAP: today this only drives a player-facing warning (see app.js
// showCapacityWarningIfPending). If housing ever becomes a HARD cap, enforce it
// where survivors are added — recruitSurvivorV4 above — and this predicate is
// the natural guard.
export function isOverHousingCapacity(campaign) {
  return activeSurvivorsV4(campaign).length > campaignHousingCapacity(campaign);
}

export function campaignFoodConsumptionMultiplier(campaign) {
  return campaignBaseRoomStats(campaign).foodConsumptionMultiplier;
}

// Food the living crew will need at the next day advance.
export function campaignDailyQuota(campaign) {
  return dailyQuota(activeSurvivorsV4(campaign).length, {
    foodConsumptionMultiplier: campaignFoodConsumptionMultiplier(campaign),
  });
}

// --- Endgame prototype --------------------------------------------------

export function campaignRescueDay(campaign) {
  return normalizeEndgame(campaign.endgame).rescueDay;
}

export function shouldShowEndgameBriefing(campaign) {
  const endgame = normalizeEndgame(campaign.endgame);
  return campaign.day >= ENDGAME_BRIEFING_DAY && !endgame.briefingAcknowledged;
}

export function acknowledgeEndgameBriefing(campaign) {
  const endgame = normalizeEndgame(campaign.endgame);
  if (endgame.briefingAcknowledged) return campaign;
  return { ...campaign, endgame: { ...endgame, briefingAcknowledged: true } };
}

// The rescue day is the campaign's terminal beat: the evac window opens and the
// horde makes its final push. Reached by day-advance, not by any threat gauge.
export function isRescueDay(campaign) {
  return campaign.day === campaignRescueDay(campaign);
}

// Stamp the run's ending (win = final wave repelled, lose = safe room fell).
// Idempotent-ish: a resolved run keeps its first outcome.
export function resolveRescue(campaign, outcome) {
  const endgame = normalizeEndgame(campaign.endgame);
  if (endgame.rescueResolved) return campaign;
  const resolved = outcome === "won" || outcome === "lost" ? outcome : null;
  return { ...campaign, endgame: { ...endgame, rescueResolved: true, outcome: resolved } };
}

// True once the day-20 defense has resolved — the run is over and can't advance.
export function campaignConcluded(campaign) {
  return normalizeEndgame(campaign.endgame).rescueResolved;
}

// --- Score --------------------------------------------------------------

// Survivors airlifted out = everyone still alive when the evac arrives.
export function survivorsExtracted(campaign) {
  return activeSurvivorsV4(campaign).length;
}

// Everyone ever on the roster. Dead survivors stay in survivorsById (only their
// status flips), so this is the lifetime headcount even once recruitment lands.
export function totalRecruited(campaign) {
  return Object.keys(campaign.survivorsById ?? {}).length;
}

// --- Per-POI threat (threat.mjs) --------------------------------------------

export function currentPoiThreat(campaign, poiId) {
  return poiThreat(campaign.poiThreatState, poiId, campaign.day);
}

// Stamp a supply run's threat onto the POI. `amount` is the run's accumulated threat
// (see tactical-turn.mjs); it stacks on the POI's decayed remainder, clamped to
// THREAT_MAX. Defaults to a single visit's worth for callers with no run total.
export function recordCampaignPoiVisit(campaign, poiId, amount) {
  return { ...campaign, poiThreatState: recordPoiVisit(campaign.poiThreatState, poiId, campaign.day, amount) };
}

// --- Global (campaign-wide) threat (threat.mjs) -----------------------------

export function currentGlobalThreat(campaign) {
  return normalizeGlobalThreat(campaign.globalThreat).value;
}

export function globalThreatCritical(campaign) {
  return hasReachedGlobalThreshold(campaign.globalThreat);
}

// --- Day ----------------------------------------------------------------

// Advance a day: feed the crew from the pantry, then tick the day. Feeding is
// the survival-pressure beat — fed survivors reset to healthy, unfed ones go
// hungry, and a survivor who was already hungry and still can't be fed starves
// (dies). Surplus food carries over. A `lastFeeding` summary is stamped for the
// UI/log to report what happened.
export function advanceCampaignDay(campaign) {
  const deposit = depositCarriedInventory(campaign);
  const depositedCampaign = deposit.campaign;
  const living = activeSurvivorsV4(depositedCampaign);
  const foodConsumptionMultiplier = campaignFoodConsumptionMultiplier(depositedCampaign);
  const quota = dailyQuota(living.length, { foodConsumptionMultiplier });
  const pantryBefore = depositedCampaign.pantry ?? 0;
  const feeding = resolveDailyFeeding(
    pantryBefore,
    living.map((survivor) => ({ id: survivor.id, hunger: survivor.hunger ?? 0 })),
    { foodConsumptionMultiplier },
  );

  const survivorsById = { ...depositedCampaign.survivorsById };
  for (const id of feeding.fed) survivorsById[id] = { ...survivorsById[id], hunger: 0 };
  for (const id of feeding.nowHungry) survivorsById[id] = { ...survivorsById[id], hunger: 1 };
  for (const id of feeding.starved) survivorsById[id] = { ...survivorsById[id], status: "dead", hunger: 0 };
  // Combat wounds recover 1 HP/day. Applied after the starved are marked dead
  // so the dead don't heal; the living mend toward full for the next defense.
  for (const [id, survivor] of Object.entries(survivorsById)) {
    if (survivor.status === "dead") continue;
    const health = survivor.health ?? { current: SURVIVOR_MAX_HEALTH, max: SURVIVOR_MAX_HEALTH };
    if (health.current < health.max) {
      survivorsById[id] = { ...survivor, health: { ...health, current: Math.min(health.max, health.current + 1) } };
    }
  }

  // Threat cools overnight: materialise/prune per-POI threat against the *new* day
  // so the map's threat circles fade, and bleed off the global gauge.
  const nextDay = depositedCampaign.day + 1;

  return {
    ...depositedCampaign,
    day: nextDay,
    pantry: feeding.pantryAfter,
    survivorsById,
    poiThreatState: decayThreat(depositedCampaign.poiThreatState, nextDay),
    globalThreat: decayGlobalThreat(depositedCampaign.globalThreat),
    lastInventoryDeposit: {
      day: depositedCampaign.day,
      ...deposit.summary,
    },
    lastFeeding: {
      day: depositedCampaign.day,
      quota,
      consumed: pantryBefore - feeding.pantryAfter,
      pantryAfter: feeding.pantryAfter,
      fed: feeding.fed,
      nowHungry: feeding.nowHungry,
      starved: feeding.starved,
    },
  };
}

// --- Base defense (base-defense-combat.mjs) -----------------------------

// A horde attacks the base periodically. Scheduled (not purely threat-
// reactive) so it's a predictable pressure beat; threat only sizes the wave.
export const DEFENSE_CADENCE_DAYS = 2;
export const DEFENSE_WAVE_BASE = 5;
// The global gauge is 0-100, NOT the 0-12 POI scale threatToEnemyCount defaults
// to (perThreat = 1/3, which would give ~38 at max). 0.1 keeps waves 5 -> 15.
export const DEFENSE_WAVE_PER_THREAT = 0.1;
export const DEFENSE_REPEL_THREAT_RELIEF = 10;

// Fires on the newly-advanced day: 2, 4, 6...
export function defenseDueOnDay(day) {
  return Number.isInteger(day) && day >= 2 && day % DEFENSE_CADENCE_DAYS === 0;
}

export function defenseWaveSize(campaign) {
  return threatToEnemyCount(currentGlobalThreat(campaign), {
    baseCount: DEFENSE_WAVE_BASE,
    perThreat: DEFENSE_WAVE_PER_THREAT,
  });
}

// The day-20 final assault. Unlike the mid-game waves (decaying gauge), this is
// sized off the NON-decaying lifetime accumulator, so a whole campaign of noisy
// noisy supply runs land here — the deterrent against recklessness. Bigger floor and a
// steeper slope than the periodic waves, capped so it stays winnable.
export const FINAL_WAVE_BASE = 10;
export const FINAL_WAVE_PER_ACCRUED_THREAT = 0.2;
export const FINAL_WAVE_MAX = 40;

export function finalWaveSize(campaign) {
  const scaled = threatToEnemyCount(campaign.threatAccrued ?? 0, {
    baseCount: FINAL_WAVE_BASE,
    perThreat: FINAL_WAVE_PER_ACCRUED_THREAT,
  });
  return Math.min(FINAL_WAVE_MAX, scaled);
}

// A survivor at 1 HP can't safely take a hit — they're benched from supply runs and
// retreat instantly at a doorway.
export function isSurvivorWounded(survivor) {
  return (survivor?.health?.current ?? SURVIVOR_MAX_HEALTH) <= 1;
}

// Apply a resolved base defense to the campaign: write back each defender's HP
// (clamped >= 1 — doorway combat structurally cannot kill), and on a repelled
// wave bleed the global threat gauge as the reward. Stamps `lastDefense` for
// the log (mirrors lastFeeding). Does NOT handle the game-over case (safe room
// taken) — that's the UI's job, since it's a run-ending flow, not state.
export function applyDefenseOutcome(campaign, { result, waveSize, rosterHealth = [], breachedDoorCount = 0, deaths = [] }) {
  const survivorsById = { ...campaign.survivorsById };
  for (const { id, health } of rosterHealth) {
    const survivor = survivorsById[id];
    if (!survivor) continue;
    const max = survivor.health?.max ?? SURVIVOR_MAX_HEALTH;
    const current = Math.max(1, Math.min(max, health?.current ?? max));
    survivorsById[id] = { ...survivor, health: { current, max } };
  }
  // Aggressive last stands can now KILL defenders -- the first time base
  // defense produces deaths (ordinary siege/retreat still can't).
  for (const id of deaths) {
    const survivor = survivorsById[id];
    if (survivor) survivorsById[id] = { ...survivor, status: "dead", health: { current: 0, max: survivor.health?.max ?? SURVIVOR_MAX_HEALTH } };
  }
  const globalThreat = result === "repelled"
    ? decayGlobalThreat(campaign.globalThreat, DEFENSE_REPEL_THREAT_RELIEF)
    : campaign.globalThreat;
  return {
    ...campaign,
    survivorsById,
    globalThreat,
    lastDefense: { day: campaign.day, result, waveSize, breachedDoorCount, deaths: [...deaths] },
  };
}

// Apply a resolved supply run's per-survivor outcomes (tactical-session.mjs's
// resolveSupplyRun -> survivorOutcomes) to campaign state: dead survivors are marked
// dead and keep NOTHING (supplies they were carrying are lost), extracted
// survivors keep their haul until the nightly base deposit.
export function applySupplyRunOutcome(campaign, { poiId, survivorOutcomes, supplyRunThreat = 0 }) {
  let next = campaign;
  for (const outcome of survivorOutcomes ?? []) {
    if (outcome.died) {
      next = setSurvivorV4Status(next, outcome.survivorId, "dead");
      const survivor = next.survivorsById[outcome.survivorId];
      if (survivor) {
        next = { ...next, survivorsById: { ...next.survivorsById, [outcome.survivorId]: { ...survivor, inventory: [] } } };
      }
      continue;
    }
    const items = outcome.items ?? [];
    const survivor = next.survivorsById[outcome.survivorId];
    if (survivor) {
      // A survivor who equipped gear mid-run keeps that loadout back at base.
      const equipmentItemIds = outcome.equipmentItemIds
        ? normalizeEquipmentItemIds(outcome.equipmentItemIds)
        : survivor.equipmentItemIds;
      // Persist combat wounds taken during the run (they recover 1 HP/day on the
      // daily tick). Clamp to the survivor's max; a survivor who reached 0 HP
      // would have died during the run, so a survivor here is at >= 1.
      const health = outcome.health
        ? {
            current: Math.max(1, Math.min(survivor.health?.max ?? outcome.health.max, outcome.health.current)),
            max: survivor.health?.max ?? outcome.health.max,
          }
        : survivor.health;
      next = {
        ...next,
        survivorsById: {
          ...next.survivorsById,
          [outcome.survivorId]: { ...survivor, inventory: [...(survivor.inventory ?? []), ...items], equipmentItemIds, health },
        },
      };
    }
  }
  // The supply run's accumulated threat lands on the POI (rendered as its threat circle,
  // driving revisit difficulty) and feeds the campaign-wide gauge. A perfectly
  // clean run (no threat generated) leaves no trace on either.
  if (poiId) next = recordCampaignPoiVisit(next, poiId, supplyRunThreat);
  next = {
    ...next,
    globalThreat: addGlobalThreat(next.globalThreat, supplyRunThreat),
    // Bank the run's threat into the lifetime accumulator too (never decays),
    // so it still weighs on the day-20 final wave long after the gauge cools.
    threatAccrued: (next.threatAccrued ?? 0) + Math.max(0, supplyRunThreat),
  };
  return next;
}

// --- Persistence --------------------------------------------------------
// The app wires these into a v4-keyed autosave when the shell lands (phase 4);
// V3 saves are intentionally not migrated (the rewrite accepts a save break).

export function serializeCampaignV4(campaign) {
  return JSON.stringify(campaign);
}

export function parseCampaignV4(json) {
  const raw = JSON.parse(json);
  if (raw?.schemaVersion !== CAMPAIGN_V4_SCHEMA_VERSION) {
    throw new Error(`Unsupported V4 campaign schema version: ${raw?.schemaVersion}.`);
  }
  return normalizeCampaignV4(raw);
}
